import { activeSubgenreCountriesWebLocales } from '@/constants/subgenres';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { currentUser, getUserCustomToken } from '@/helpers/user-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useFooterStore } from '@/stores/pinia/useFooterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { withRetry } from '@/helpers/retry-helper';
import Cookies from 'js-cookie';
import { type NuxtApp } from 'nuxt/app';
import { JWID_COOKIE } from '@/nuxtPlugins/02.core-env';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { loadOnBoardingStatus } from '@/helpers/onboarding-helper';

export default defineNuxtPlugin({
	name: 'session-prefetch',
	dependsOn: ['core-env', 'services-setup'],
	parallel: true,
	async setup(nuxtApp) {
		const context = useNuxtApp();
		const runtimeConfig = useRuntimeConfig();
		const url = useRequestURL().pathname;
		const appStore = useAppStore(context.$pinia);

		const languageStore = useLanguageStore(context.$pinia);
		const constantStore = useConstantStore(context.$pinia);
		const userStore = useUserStore(context.$pinia);
		const filterStore = useFilterStore(context.$pinia);

		const parallelFetches: Promise<unknown>[] = [];
		const initLanguagePromise = languageStore.initLanguage();
		parallelFetches.push(initLanguagePromise);
		const $apollo = useNuxtApp().$apolloClients.default;

		if (import.meta.server || !runtimeConfig.public.ssrMode) {
			parallelFetches.push(constantStore.fetchPackages($apollo));
		}

		if (import.meta.server && userStore.jwId && !url.includes('/refresh')) {
			parallelFetches.push(userStore.loadSettings());
		}

		if (import.meta.server || !runtimeConfig.public.ssrMode) {
			parallelFetches.push(constantStore.fetchConstants());
			if (activeSubgenreCountriesWebLocales.includes(languageStore.webLocale)) {
				parallelFetches.push(constantStore.fetchSubgenres());
			}
			const footerStore = useFooterStore(context.$pinia);
			// all requests that are used on pretty much all pages and are un-personalised and cacheable
			// making sure that we got all uncritical requests done before we ship HTML back
			parallelFetches.push(footerStore.fetchTopTitles());
			parallelFetches.push(footerStore.fetchTopArticles());
		}

		watch(
			() => languageStore.webLocale,
			(webLocale, oldWebLocale) => {
				if (webLocale === oldWebLocale || import.meta.server) return;
				constantStore.fetchPackages($apollo);
			},
			{ immediate: false }
		);

		TrackingHelper.resetState();

		await initClientSession({
			context,
			userStore,
		});

		/*    
//TODO NUXT: support SERVER_ENV
if (import.meta.server) {
// clear SERVER_ENV to avoid leaks
delete context.ssr.data.SERVER_ENV;
} */

		appStore.setSeoUser(context.$ssrData.isSeoUser);

		if (import.meta.server) {
			// xServeTrack is only available on the server
			// context.ssrData is overwritten by the client
			appStore.setXServeTrack(context.$ssrData.xServeTrack);
		}

		// NOTE(valerio.mazza): failsafe for useFilterStore to remove after a proper refactor of the useFilterStore
		if (filterStore.getWebLocale(languageStore.webLocale) == null) {
			// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
			filterStore.initFiltersPerLocale(languageStore.webLocale);
		}

		if (import.meta.client && userStore.jwId && !url.includes('/refresh')) {
			await userStore.loadSettings();
		}

		if (!userStore.onboardingStatus) {
			loadOnBoardingStatus(languageStore.webLocale, url);
		}
		const s = getHrTime();
		await Promise.all(parallelFetches);
		recordSsrPhase(context, 'await-parallel-fetches', s);
		nuxtApp.provide('language', languageStore.overriddenLanguage);
	},
});

async function initClientSession({
	context,
	userStore,
}: {
	userStore: ReturnType<typeof useUserStore>;
	context: NuxtApp;
}) {
	if (import.meta.client) {
		// jwId client handover needs to happen before we await for refreshTokenPromise,
		// because if there is an error with the refresh token, it might await endlessly
		const jwId =
			// If we already have a jwId in the store (e.g., synced from cookies on subdomains)
			// then it is the most up to date
			userStore.jwId ??
			// otherwise check the cookie as the cross-domain source of truth
			Cookies.get(JWID_COOKIE) ??
			// finally fallback to localStorage
			JSON.parse(localStorage.getItem('jw/user') ?? 'null')?.jwId;

		userStore.setJwId(jwId);

		if (userStore.isLoggedIn) {
			// Wait for the refreshToken promise to finish Apollo init (it needs a fresh access token).
			// This order is important, we want to try refresh the token before initializing the store
			// and making the requests to loadList and loadSettings. The user would be logged out if the refreshToken fails
			const token = await userStore.refreshToken();

			// If token isn't returned then there was an error returning token
			if (!token) {
				context.provide('showRefreshTokenError', true);

				TrackingHelper.trackEvent('refresh_token_error', {
					action: 'main_ssr_refresh',
				});
			}
		}

		if (userStore.accessToken) {
			const user = await currentUser();
			if (user == null) {
				const accessToken = userStore.accessToken;
				const jwId = userStore.jwId as string;

				let allRetriesFailed = false;
				let finalError: Error | undefined;

				const maxAttempts = 3;

				await withRetry(
					async () => getUserCustomToken(accessToken, jwId),
					async ({ attempt, maxAttempts, error }) => {
						const message =
							error == null
								? `[main.ssr.ts] getUserCustomToken returned undefined (attempt ${attempt}/${maxAttempts})`
								: `[main.ssr.ts] getUserCustomToken threw error (attempt ${attempt}/${maxAttempts})`;

						captureMessageForSentry(
							message,
							{
								error,
								accessToken: accessToken?.substring(0, 20) + '...',
								jwId,
								where: 'main.ssr.ts initFirebaseUser',
							},
							attempt >= maxAttempts ? 'error' : 'warning'
						);

						if (attempt >= maxAttempts) {
							allRetriesFailed = true;
							finalError = error;
						}
					},
					{ maxAttempts }
				);

				if (allRetriesFailed) {
					captureMessageForSentry(
						'[main.ssr.ts] Failed to establish Firebase session after retries - logging out',
						{
							error: finalError,
							jwLoginId: userStore.jwLoginId,
							attempts: maxAttempts,
							where: 'main.ssr.ts initFirebaseUser',
						},
						'error'
					);

					await userStore.logout();
				}
			}
		}
	}
}
